export interface ParsedMessage {
  brief: string | null;
  body: string;
  suggestions: string[];
  pagination: { showing: number; total: number } | null;
}

export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  search_rsds: 'Searching releases...',
  get_rsd_details: 'Loading release details...',
  search_milestones: 'Checking milestones...',
  search_features: 'Searching features...',
  get_audit_trail: 'Loading change history...'
};

export function getToolDisplayName(tool: string): string {
  return TOOL_DISPLAY_NAMES[tool] ?? 'Working...';
}

export function extractAssistantBrief(text: string): {
  brief: string | null;
  body: string;
} {
  const normalized = text.replace(/\r\n/g, '\n');
  const open = ':::brief\n';
  if (!normalized.startsWith(open)) {
    return { brief: null, body: text };
  }
  const closeToken = '\n:::\n';
  const closeIdx = normalized.indexOf(closeToken, open.length);
  if (closeIdx === -1) {
    return { brief: null, body: text };
  }
  const brief = normalized
    .slice(open.length, closeIdx)
    .trim()
    .replace(/\s+/g, ' ');
  const body = normalized
    .slice(closeIdx + closeToken.length)
    .replace(/^\n+/, '');
  return { brief, body };
}

// Strips leading emoji / pictograph characters from a string.
function stripLeadingEmoji(text: string): string {
  return text
    .replace(
      /^[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{2300}-\u{23FF}\u{2B50}\u{2B55}]+\s*/gu,
      ''
    )
    .trim();
}

export function extractSuggestions(content: string): {
  body: string;
  suggestions: string[];
} {
  const marker = '**Suggested next questions:**';
  const idx = content.lastIndexOf(marker);
  if (idx === -1) return { body: content, suggestions: [] };

  const body = content.slice(0, idx).trimEnd();
  const tail = content.slice(idx + marker.length);
  const suggestions = tail
    .split('\n')
    .map(l =>
      stripLeadingEmoji(
        l
          .replace(/^[-*]\s*/, '')
          .replace(/^\[(.+)\]$/, '$1')
          .trim()
      )
    )
    .filter(Boolean);

  return { body, suggestions };
}

export function extractPagination(
  content: string
): { showing: number; total: number } | null {
  const match = content.match(/[Ss]howing (\d+) of (\d+)/);
  if (!match) return null;
  return { showing: parseInt(match[1], 10), total: parseInt(match[2], 10) };
}

// Returns a stable view model for a completed assistant message.
export function parseAssistantMessage(content: string): ParsedMessage {
  const { brief, body: afterBrief } = extractAssistantBrief(content);
  const { body, suggestions } = extractSuggestions(afterBrief);
  const pagination = extractPagination(body);
  return { brief, body, suggestions, pagination };
}

// Partial parse for a still-streaming message — brief and pagination can appear before streaming ends.
export function parseStreamingMessage(
  content: string
): Pick<ParsedMessage, 'brief' | 'body' | 'pagination'> {
  const { brief, body } = extractAssistantBrief(content);
  const pagination = extractPagination(body);
  return { brief, body, pagination };
}
